"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.informationResponseFunctionForQuestion = exports.informationResponseFunctionForAnswer = exports.informationCharacteristicCurve4pl = exports.negativeLogLikelihood = exports.logLikelihood = exports.gradedResponseModel2pl = exports.invLogisticIrt2pl = exports.ilogt = exports.erf = exports.getParamsByValueOrder = exports.getParamsFromConfig = void 0;
// take an exampleObject and for each variable, add a property called `params`
// which is an array of `params` which are triples of (alpha, beta1, beta2)
// The first element is (alpha, undefined, beta)
// the middle elements are (alpha, beta of the item before, beta)
// the final element is (alpha, beta of the item before, undefined)
const keyBy_1 = __importDefault(require("lodash/keyBy"));
/**
 * @typedef {import('./types').IRTConfig} IRTConfig
 */
/**
 * @typedef {import('./types').Response} Response
 */
/**
 * @typedef {import('./types').OptionalNumber} OptionalNumber
 */
/**
 * @typedef {import('./types').IRTParams} IRTParams
 */
/**
 * @param {{ beta: OptionalNumber }} x
 * @param {{ beta: OptionalNumber }} y
 * @returns {number}
 */
function compareCoefficients(x, y) {
    if (x.beta === null && y.beta === null) {
        return 0;
    }
    if (x.beta === null) {
        return -1;
    }
    if (y.beta === null) {
        return 1;
    }
    return x.beta - y.beta;
}
/**
 * @param {IRTConfig} config
 * @returns {IRTParams}
 */
function getParamsFromConfig(config) {
    /** @type {IRTParams} */
    const params = {};
    Object.keys(config).forEach((key) => {
        const rawVariable = config[key];
        // Treat α<0 as a sign-convention artifact and normalize to +|α|.
        // Validated empirically: every α<0 item in the production calibration is
        // a forward-keyed positive statement ("I am able to…", "I enjoy…"), so
        // negative α here is a calibration-import artifact, not legitimate
        // reverse-keying. Sign-flip alone reproduces Promin's T-scores within
        // ~10 T-points across PQ/MQ/SQ on the validation spreadsheet.
        const variable = Object.assign(Object.assign({}, rawVariable), { alpha: Math.abs(rawVariable.alpha) });
        const { alpha, coefficients } = variable;
        // Validate β monotonicity in value order — but only when value labels are
        // numeric ordinal scores (0..K-1). For abstract labels ('a', 'b', …) the
        // notion of "increasing value" doesn't apply and the engine's internal
        // beta-sort handles ordering correctly anyway.
        //
        // After Math.abs(α), all items are forward-keyed and a numeric ordinal
        // calibration requires β to be non-decreasing as value increases.
        // Misalignment (e.g. value=4 paired with smaller β than value=3) silently
        // produces wrong-direction T-scores via the engine's beta-sort path —
        // catch it here at config-load time. See PQ "all best" cap-at-T=52 bug
        // for the canonical example.
        const allNumeric = coefficients.every((c) => c.value != null && /^-?\d+$/.test(String(c.value)));
        if (allNumeric) {
            const byValueOrder = [...coefficients].sort((a, b) => parseInt(a.value, 10) - parseInt(b.value, 10));
            /** @type {number[]} */
            const nonNullBetas = [];
            byValueOrder.forEach((c) => {
                if (c.beta !== null && c.beta !== undefined)
                    nonNullBetas.push(c.beta);
            });
            for (let i = 1; i < nonNullBetas.length; i += 1) {
                if (nonNullBetas[i] < nonNullBetas[i - 1]) {
                    throw new Error(`Item "${key}": β not monotone non-decreasing in value order. ` +
                        `Got [${nonNullBetas.join(', ')}]. ` +
                        `Forward-keyed items require β to increase with response value; ` +
                        `swap the affected coefficients in the calibration JSON.`);
                }
            }
        }
        const sorted = [...coefficients].sort(compareCoefficients);
        /** @type {IRTParams['string']} */
        const variableParams = {};
        /** @type {OptionalNumber} */
        let beta1;
        /** @type {OptionalNumber} */
        let beta2;
        let i = 0;
        let j = 1;
        while (j <= sorted.length - 1) {
            if (sorted[i].beta === sorted[j].beta) {
                j += 1;
            }
            else {
                const coefficient = sorted[i];
                const coefficientNext = sorted[j];
                beta1 = coefficient.beta;
                beta2 = coefficientNext.beta;
                for (let k = i; k < j; k += 1) {
                    variableParams[sorted[k].value] = [alpha, beta1, beta2];
                }
                i = j;
                j += 1;
            }
        }
        if (i <= sorted.length - 1 &&
            sorted[i].beta === sorted[sorted.length - 1].beta) {
            beta1 = sorted[sorted.length - 1].beta;
            for (let k = i; k <= sorted.length - 1; k += 1) {
                variableParams[sorted[k].value] = [alpha, beta1, null];
            }
        }
        // variableParams.push([alpha, beta1, undefined]);
        params[key] = variableParams;
    });
    return params;
}
exports.getParamsFromConfig = getParamsFromConfig;
/**
 * Build IRT params preserving the calibration's authored value→β mapping.
 * Coefficients are ordered by numeric `value` (0, 1, 2, …, K) and consecutive
 * pairs become (β1, β2) for each category — no β-sorting, no α normalization.
 *
 * Use with SHAW-CAT-style banks where β anchors are rating-scale-shared and α
 * may be negative (intentional reverse-keying). Pair with
 * `applyClinicalScoreMap` at the questionnaire layer so the patient's clinical
 * response is flipped on negative-α items before the engine consumes it. The
 * negative α is preserved here because the engine math produces valid
 * probabilities when the clinical-flip + α-sign convention align.
 *
 * For PROMIS-style banks with monotonic β and α≥0, this produces identical
 * results to `getParamsFromConfig`.
 *
 * @param {IRTConfig} config
 * @returns {IRTParams}
 */
function getParamsByValueOrder(config) {
    /** @type {IRTParams} */
    const params = {};
    Object.keys(config).forEach((key) => {
        const variable = config[key];
        const { alpha, coefficients } = variable;
        const ordered = [...coefficients].sort((a, b) => {
            const av = Number(a.value);
            const bv = Number(b.value);
            if (Number.isNaN(av) || Number.isNaN(bv))
                return 0;
            return av - bv;
        });
        /** @type {IRTParams['string']} */
        const variableParams = {};
        for (let k = 0; k < ordered.length; k += 1) {
            const beta1 = ordered[k].beta;
            const beta2 = k + 1 < ordered.length ? ordered[k + 1].beta : null;
            variableParams[ordered[k].value] = [alpha, beta1, beta2];
        }
        params[key] = variableParams;
    });
    return params;
}
exports.getParamsByValueOrder = getParamsByValueOrder;
/**
 * Taylor series approximation of the error function erf.
 * This is approximated by
 * erf(x) ~= 2/sqrt(pi) * Sigma_0^inf(((-1^n)(z^(2n+1) )/n!(2n+1)))
 * https://en.wikipedia.org/wiki/Error_function
 *
 * the values for approximation are
 * a=-1^n
 * b=2n+1
 * c=z^(b)
 * d=n!
 *
 * so that forms erf(x) = 2/sqrt(pi) * Sigma (ac/bd)
 *
 * where s is the intermediate sum
 *
 * @param {number} z - value to calculate erf for
 * @param {number} approxLevel - number of iterations to use in approximation (integer)
 * @returns number
 */
function erf(z, approxLevel = 15) {
    let a = 1;
    let b = 1;
    let d = 1;
    let s = z;
    for (let i = 1; i < approxLevel; i += 1) {
        a *= -1;
        b += 2;
        const c = z ** b;
        d *= i;
        s += (a * c) / (b * d);
    }
    return (2 / Math.sqrt(Math.PI)) * s;
}
exports.erf = erf;
/**
 *
 * The inverse logistic function
 *  e^x/(1+e^x)
 * @param {number} x
 * @returns number
 */
function ilogt(x) {
    return Math.exp(x) / (Math.exp(x) + 1);
}
exports.ilogt = ilogt;
/**
 *
 * @param {number} theta
 * @param {number} alpha
 * @param {number} beta
 * @returns number
 */
function invLogisticIrt2pl(theta, alpha, beta) {
    const exponent = alpha * (theta - beta);
    const probability = 1 / (1 + Math.exp(-exponent));
    return probability;
}
exports.invLogisticIrt2pl = invLogisticIrt2pl;
// I think this is an equivalent implementation, and has been tested, but the
// above is easier to read.
// export function invLogisticIrt2pl(theta, alpha, beta) {
//   const p = alpha * (theta - beta);
//   return ilogt(p);
// }
/**
 * Samejima 2PL Graded Response Model probability for a single response.
 *
 * @deprecated For Andrich RSM calibrations (SHAW-CAT and any rating-scale
 *   bank where item discrimination is fixed at 1.0), use the functions in
 *   `ratingScale.js` instead. The dispatcher in `irtMethods.js` routes
 *   RSM-class calibrations away from this function automatically. This
 *   function remains active for PROMIS-style per-item-β calibrations.
 *
 * @param {number} theta
 * @param {number} alpha
 * @param {OptionalNumber} beta1
 * @param {OptionalNumber} beta2
 * @returns number
 */
function gradedResponseModel2pl(theta, alpha, beta1, beta2) {
    let result;
    // We will use `undefined` to refer to +/- infinity for the bounds
    // It is not possible to cover both sides of the interval, so it
    // can be one-sided open only.
    if (beta1 === null && typeof beta2 === 'number') {
        result = 1 - invLogisticIrt2pl(theta, alpha, beta2);
    }
    else if (beta2 === null && typeof beta1 === 'number') {
        result = invLogisticIrt2pl(theta, alpha, beta1);
    }
    else if (typeof beta1 === 'number' && typeof beta2 === 'number') {
        result =
            invLogisticIrt2pl(theta, alpha, beta1) -
                invLogisticIrt2pl(theta, alpha, beta2);
        // For α<0 with ascending β (the SHAW-CAT BY_VALUE convention), the naive
        // difference is negative because σ is decreasing in α(θ−β) when α<0.
        // The σ(-x)=1-σ(x) identity gives |result| as the correct probability:
        //   P(Y=v | α<0) = σ(α(θ-β1)) - σ(α(θ-β2))
        //               = (1-σ(-α(θ-β1))) - (1-σ(-α(θ-β2)))
        //               = σ(-α(θ-β2)) - σ(-α(θ-β1))   = |original|
        // For α≥0 with ascending β (PROMIS convention), result is already ≥ 0
        // so abs is a no-op.
        if (result < 0) {
            result = -result;
        }
    }
    else {
        // Throw an error
        throw new Error('Beta 1 or Beta 2 need to be numbers');
    }
    if (result < 0 || result > 1) {
        throw new Error('GRM should return a probability');
    }
    return result;
}
exports.gradedResponseModel2pl = gradedResponseModel2pl;
/**
 * Calculates the log likelihood of theta given the responses
 * and the calibrations
 * @param {number} theta
 * @param {Response[]} responses
 * @param {IRTParams} params
 */
function logLikelihood(theta, responses, params) {
    if (responses.length === 0) {
        // console.warn(
        //   `there are no responses for log likelihood - all results yield 0`,
        //   {
        //     code: 'no responses for log likelihood',
        //     detail: `responses are - ${responses} \n\n params are ${params}`,
        //   },
        // );
        return 0;
    }
    let ll = 0;
    responses.forEach((response) => {
        const correspondingParams = params[response.questionId] &&
            params[response.questionId][response.answer.value];
        if (!correspondingParams) {
            // throw new Error(
            //   `The parameters must exist for a given question. No parameters for questionId ${response.questionId}`,
            // );
            return;
        }
        const [alpha, beta1, beta2] = correspondingParams;
        // console.log(params[response.questionId]);
        // console.log(response);
        // console.log(correspondingParams);
        ll += Math.log(gradedResponseModel2pl(theta, alpha, beta1, beta2));
    });
    if (ll === 0 && responses.length > 0) {
        // console.log('throwing ll=0');
        // console.log(responses);
        // console.log(params);
        // console.warn(
        //   `Log likelihood is still 0 - would generally expect this to be >0`,
        //   {
        //     code: 'LL is zero',
        //     detail: `responses are - ${responses} \n\n params are ${params}`,
        //   },
        // );
    }
    return ll;
}
exports.logLikelihood = logLikelihood;
/**
 * Calculates the negative log likelihood of theta given the responses
 * This is useful for minimising functions - the minimum of the negative
 * log likelihood is the maximum of the log likelihood which is maximum likelihood
 * @param {number} theta
 * @param {Response[]} responses
 * @param {IRTParams} params
 * @returns number
 */
function negativeLogLikelihood(theta, responses, params) {
    return -logLikelihood(theta, responses, params);
}
exports.negativeLogLikelihood = negativeLogLikelihood;
/**
 * @param {number} theta
 * @param {number} a
 * @param {number} b
 * @param {number} c
 * @param {number} d
 * @returns {number}
 */
function informationCharacteristicCurve4pl(theta, a, b, c = 0, d = 1) {
    return c + (d - c) / (1 + Math.exp(-a * (theta - b)));
}
exports.informationCharacteristicCurve4pl = informationCharacteristicCurve4pl;
/**
 *
 * @param {number} theta - estimate of ability
 * @param {number} alpha - Difficulty of question
 * @param {number} beta - Difficulty level of response
 * @returns {number}
 */
function informationResponseFunctionForAnswer(theta, alpha, beta) {
    const p = invLogisticIrt2pl(theta, alpha, beta);
    const q = 1 - p;
    return alpha * alpha * p * q;
}
exports.informationResponseFunctionForAnswer = informationResponseFunctionForAnswer;
/**
 * Compute the amount of information given by a question for a given theta.
 * This is the sum of the information responses for each individual level
 * of the question . \Sigma_questions I(q|theta)
 * @param {number} theta - The theta for which you are calculating the information
 * @param {IRTParams['string']} questionParams - array of item parameters as triples of [alpha, beta_1, beta_2]
 * @returns {number} information
 */
function informationResponseFunctionForQuestion(theta, questionParams) {
    let information = 0;
    // NOTE: If value at index '1' is the same it means that
    //       corresponding responses had the same "beta" coefficient
    //       and we collapsed them to a single logical response.
    Object.values((0, keyBy_1.default)(questionParams, '1')).forEach((param) => {
        const [alpha, beta1] = param;
        // Getting the lowest level correct is uninformative as q(x|the) = 0
        // and so information must, by definition, be 0
        if (typeof beta1 === 'number') {
            information += informationResponseFunctionForAnswer(theta, alpha, beta1);
        }
    });
    return information;
}
exports.informationResponseFunctionForQuestion = informationResponseFunctionForQuestion;
//# sourceMappingURL=utilities.js.map